import React, { useState } from 'react';
import { FaFlask, FaLaptopCode, FaBuilding, FaChartLine, FaMicroscope, FaBook, FaUserGraduate } from 'react-icons/fa';
import './department.css';

const Department = () => {
  const [activeDept, setActiveDept] = useState('engineering');

  const departments = [
    {
      id: 'engineering',
      name: 'Engineering & Technology',
      icon: <FaFlask />,
      description: 'Our engineering programs combine theoretical knowledge with hands-on experience to prepare students for technological challenges.',
      programs: [
        'B.Sc. in Mechanical Engineering',
        'B.Sc. in Electrical Engineering',
        'B.Sc. in Civil Engineering',
        'Diploma in Automotive Technology'
      ],
      faculty: [
        { name: 'Prof. Anura Perera', position: 'Head of Department', expertise: 'Thermodynamics, Energy Systems' },
        { name: 'Dr. Nimali Fernando', position: 'Senior Lecturer', expertise: 'Structural Engineering' },
        { name: 'Dr. Rajesh Kumar', position: 'Associate Professor', expertise: 'Power Systems' }
      ],
      facilities: [
        'Advanced Engineering Labs',
        'CAD/CAM Center',
        'Robotics Workshop',
        'Materials Testing Facility'
      ]
    },
    {
      id: 'computing',
      name: 'Computing & Information Systems',
      icon: <FaLaptopCode />,
      description: 'Developing the next generation of IT professionals through cutting-edge curriculum and industry partnerships.',
      programs: [
        'B.Sc. in Computer Science',
        'B.Sc. in Information Technology',
        'Diploma in Software Engineering',
        'M.Sc. in Data Science'
      ],
      faculty: [
        { name: 'Prof. Sunil Bandara', position: 'Head of Department', expertise: 'Artificial Intelligence' },
        { name: 'Dr. Priyanka Silva', position: 'Senior Lecturer', expertise: 'Cybersecurity' },
        { name: 'Dr. Kamal Gunawardena', position: 'Associate Professor', expertise: 'Cloud Computing' }
      ],
      facilities: [
        'High-Performance Computing Lab',
        'Cybersecurity Center',
        'Innovation Hub',
        'Mobile Development Studio'
      ]
    },
    {
      id: 'business',
      name: 'Business & Management',
      icon: <FaChartLine />,
      description: 'Developing business leaders with global perspective through practical, industry-relevant education.',
      programs: [
        'BBA in Business Administration',
        'B.Sc. in Accounting & Finance',
        'Diploma in Digital Marketing',
        'MBA in International Business'
      ],
      faculty: [
        { name: 'Prof. Ranjith Dissanayake', position: 'Head of Department', expertise: 'Strategic Management' },
        { name: 'Dr. Shyama Fernando', position: 'Senior Lecturer', expertise: 'Financial Analytics' },
        { name: 'Dr. Amal Rathnayake', position: 'Associate Professor', expertise: 'Entrepreneurship' }
      ],
      facilities: [
        'Trading Simulation Lab',
        'Case Study Center',
        'Entrepreneurship Incubator',
        'Business Communication Studio'
      ]
    },
    {
      id: 'applied-sciences',
      name: 'Applied Sciences',
      icon: <FaMicroscope />,
      description: 'Bridging scientific theory and practical application through innovative research and education.',
      programs: [
        'B.Sc. in Biotechnology',
        'B.Sc. in Food Science',
        'Diploma in Laboratory Technology',
        'M.Sc. in Environmental Science'
      ],
      faculty: [
        { name: 'Prof. Geethani Peiris', position: 'Head of Department', expertise: 'Molecular Biology' },
        { name: 'Dr. Sameera Wijesinghe', position: 'Senior Lecturer', expertise: 'Food Chemistry' },
        { name: 'Dr. Nadeesha Gamage', position: 'Associate Professor', expertise: 'Environmental Biotechnology' }
      ],
      facilities: [
        'Biosafety Level 2 Lab',
        'Food Processing Center',
        'Environmental Testing Lab',
        'Research Greenhouses'
      ]
    }
  ];

  const researchAreas = {
    engineering: [
      'Renewable Energy Systems',
      'Smart Materials',
      'Automotive Engineering',
      'Structural Health Monitoring'
    ],
    computing: [
      'Machine Learning Applications',
      'Blockchain Technology',
      'IoT Systems',
      'Human-Computer Interaction'
    ],
    business: [
      'Emerging Markets',
      'Sustainable Business',
      'FinTech Innovations',
      'Consumer Behavior Analytics'
    ],
    'applied-sciences': [
      'Biopharmaceuticals',
      'Food Safety Technologies',
      'Environmental Remediation',
      'Industrial Biotechnology'
    ]
  };

  return (
    <div className="department-page">
      {/* Hero Section */}
      <section className="department-hero">
        <div className="hero-content">
          <h1>Academic Departments</h1>
          <p>Explore our diverse academic units offering cutting-edge programs and research opportunities</p>
        </div>
      </section>

      {/* Department Navigation */}
      <section className="department-nav">
        <div className="container">
          <div className="dept-tabs">
            {departments.map(dept => (
              <button
                key={dept.id}
                className={`dept-tab ${activeDept === dept.id ? 'active' : ''}`}
                onClick={() => setActiveDept(dept.id)}
              >
                <span className="tab-icon">{dept.icon}</span>
                {dept.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Department Details */}
      <section className="department-details">
        <div className="container">
          {departments.filter(dept => dept.id === activeDept).map(dept => (
            <div key={dept.id} className="dept-content">
              <div className="dept-header">
                <h2>{dept.name}</h2>
                <p className="dept-description">{dept.description}</p>
              </div>

              <div className="dept-grid">
                {/* Programs Offered */}
                <div className="dept-card programs-card">
                  <h3>
                    <FaBook className="card-icon" />
                    Programs Offered
                  </h3>
                  <ul>
                    {dept.programs.map((program, index) => (
                      <li key={index}>{program}</li>
                    ))}
                  </ul>
                  <button className="outline-button">View All Programs</button>
                </div>

                {/* Faculty Highlights */}
                <div className="dept-card faculty-card">
                  <h3>
                    <FaUserGraduate className="card-icon" />
                    Faculty Highlights
                  </h3>
                  <div className="faculty-list">
                    {dept.faculty.map((member, index) => (
                      <div key={index} className="faculty-member">
                        <h4>{member.name}</h4>
                        <p className="position">{member.position}</p>
                        <p className="expertise">{member.expertise}</p>
                      </div>
                    ))}
                  </div>
                  <button className="outline-button">Meet All Faculty</button>
                </div>

                {/* Research Areas */}
                <div className="dept-card research-card">
                  <h3>
                    <FaFlask className="card-icon" />
                    Research Areas
                  </h3>
                  <ul>
                    {researchAreas[dept.id].map((area, index) => (
                      <li key={index}>{area}</li>
                    ))}
                  </ul>
                  <button className="outline-button">Explore Research</button>
                </div>

                {/* Facilities */}
                <div className="dept-card facilities-card">
                  <h3>
                    <FaBuilding className="card-icon" />
                    Facilities
                  </h3>
                  <ul>
                    {dept.facilities.map((facility, index) => (
                      <li key={index}>{facility}</li>
                    ))}
                  </ul>
                  <button className="outline-button">Virtual Tour</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Department Spotlight */}
      <section className="spotlight-section">
        <div className="container">
          <h2>Department Spotlight</h2>
          <div className="spotlight-content">
            <div className="spotlight-image">
              <img 
                src={`/images/${activeDept}-spotlight.jpg`} 
                alt={`${departments.find(d => d.id === activeDept).name} Spotlight`} 
              />
            </div>
            <div className="spotlight-text">
              <h3>Innovation in {departments.find(d => d.id === activeDept).name}</h3>
              <p>
                Our {departments.find(d => d.id === activeDept).name.toLowerCase()} department leads in pioneering research and industry collaboration. 
                Recent achievements include:
              </p>
              <ul>
                <li>Patent for innovative solar panel technology (Engineering)</li>
                <li>National award for AI research project (Computing)</li>
                <li>Partnership with leading multinational corporations (Business)</li>
                <li>Breakthrough in biodegradable packaging (Applied Sciences)</li>
              </ul>
              <button className="cta-button">Learn About Our Achievements</button>
            </div>
          </div>
        </div>
      </section>

      {/* Student Experiences */}
      <section className="testimonial-section">
        <div className="container">
          <h2>Student Experiences</h2>
          <div className="testimonials">
            <div className="testimonial-card">
              <div className="testimonial-text">
                "The hands-on approach in the engineering department gave me the practical skills I needed to secure my dream job at a leading automotive company."
              </div>
              <div className="student-info">
                <img src="/images/student1.jpg" alt="Student" />
                <div>
                  <h4>Sanjaya Rathnayake</h4>
                  <p>B.Sc. Mechanical Engineering, 2022</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-text">
                "The business program's industry connections provided incredible internship opportunities that shaped my career path."
              </div>
              <div className="student-info">
                <img src="/images/student2.jpg" alt="Student" />
                <div>
                  <h4>Nayana Silva</h4>
                  <p>BBA, 2021</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Department */}
      <section className="contact-dept-section">
        <div className="container">
          <div className="contact-card">
            <h2>Contact the {departments.find(d => d.id === activeDept).name}</h2>
            <div className="contact-info">
              <p><strong>Department Office:</strong> Building {activeDept === 'engineering' ? 'A' : activeDept === 'computing' ? 'B' : 'C'}, Room 201</p>
              <p><strong>Phone:</strong> +94 112 345 67{activeDept === 'engineering' ? '8' : activeDept === 'computing' ? '9' : '0'}</p>
              <p><strong>Email:</strong> {activeDept}@apc.edu.lk</p>
              <p><strong>Office Hours:</strong> Mon-Fri, 8:30 AM - 4:30 PM</p>
            </div>
            <button className="cta-button">Schedule an Appointment</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Department;
